import { Box, HStack, VStack, Text, Button, Badge, SimpleGrid, Divider, IconButton } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { AvatarInitials, CurrencyText } from "../../../shared/ui";
import { formatBRL } from "../../../shared/lib/currency";
import { Pencil, Trash2, Star } from "../../../shared/icons";
import { rotuloTipoVinculo } from "../api/vinculosApi";
import VinculoColorDot from "./VinculoColorDot";
import DirectionsMenu from "./DirectionsMenu";

function MiniMetrica({ rotulo, children }) {
  return (
    <VStack spacing={0} align="flex-start">
      <Text fontSize="10px" color="app-muted-fg" textTransform="uppercase" letterSpacing="wider">
        {rotulo}
      </Text>
      <Text fontSize="sm" fontWeight="semibold">
        {children}
      </Text>
    </VStack>
  );
}

export default function VinculoCard({ vinculo, onEdit, onDelete, onToggleFavorite }) {
  const navigate = useNavigate();

  return (
    <Box bg="app-card" border="1px solid" borderColor="app-border" borderRadius="xl" boxShadow="sm" p={5}>
      <HStack spacing={3} mb={4}>
        <AvatarInitials name={vinculo.nome} size="md" />
        <VStack align="flex-start" spacing={0.5} flex={1} minW={0}>
          <HStack spacing={2} maxW="100%">
            <VinculoColorDot cor={vinculo.cor} />
            <Text fontWeight="semibold" noOfLines={1}>
              {vinculo.nome}
            </Text>
          </HStack>
          <HStack spacing={2}>
            <Badge variant="statusSubtle" colorScheme="gray" fontSize="10px">
              {rotuloTipoVinculo(vinculo.tipo)}
            </Badge>
            {vinculo.cnpj ? (
              <Text fontSize="xs" color="app-muted-fg">
                {vinculo.cnpj}
              </Text>
            ) : null}
          </HStack>
        </VStack>
        {vinculo.plantoes_atraso > 0 ? (
          <Badge variant="statusSubtle" colorScheme="danger">
            {vinculo.plantoes_atraso} em atraso
          </Badge>
        ) : null}
        {onToggleFavorite ? (
          <IconButton
            aria-label={vinculo.favorito ? "Desmarcar como favorito" : "Marcar como favorito"}
            aria-pressed={Boolean(vinculo.favorito)}
            title={vinculo.favorito ? "Favorito — aparece primeiro nas listas" : "Marcar como favorito"}
            icon={<Star size={18} fill={vinculo.favorito ? "currentColor" : "none"} />}
            size="sm"
            variant="ghost"
            color={vinculo.favorito ? "warning.500" : "app-muted-fg"}
            onClick={() => onToggleFavorite(vinculo)}
          />
        ) : null}
      </HStack>

      <SimpleGrid columns={3} spacing={3} mb={vinculo.valor_fim_de_semana != null ? 2 : 4}>
        <MiniMetrica rotulo="Por plantão">
          <CurrencyText value={vinculo.valor_plantao} />
        </MiniMetrica>
        <MiniMetrica rotulo="Por hora">
          <CurrencyText value={vinculo.valor_hora} />
        </MiniMetrica>
        <MiniMetrica rotulo="Paga dia">{vinculo.dia_pagamento}</MiniMetrica>
      </SimpleGrid>
      {vinculo.valor_fim_de_semana != null ? (
        <Text fontSize="xs" color="app-muted-fg" mb={4}>
          Sábados e domingos: {formatBRL(vinculo.valor_fim_de_semana)}
        </Text>
      ) : null}

      <Divider mb={4} />

      <HStack justify="space-between" mb={4}>
        <VStack spacing={0} align="flex-start">
          <Text fontSize="xs" color="app-muted-fg">
            Total no mês
          </Text>
          <Text fontWeight="bold">
            <CurrencyText value={vinculo.total_no_mes} />
          </Text>
        </VStack>
        <Text fontSize="sm" color="app-muted-fg">
          {vinculo.plantoes_no_mes} plantão(ões)
        </Text>
      </HStack>

      <HStack spacing={2} flexWrap="wrap">
        <Button size="sm" variant="outline" flex={1} onClick={() => navigate(`/vinculos/${vinculo.id}`)}>
          Ver detalhes
        </Button>
        <Button size="sm" variant="secondary" flex={1} onClick={() => navigate(`/relatorios?vinculo=${vinculo.id}`)}>
          Gerar comprovação
        </Button>
      </HStack>

      <HStack spacing={1} mt={2} flexWrap="wrap">
        <DirectionsMenu endereco={vinculo.endereco} flex="1 0 auto" />
        <Button size="sm" variant="ghost" flex="1 0 auto" leftIcon={<Pencil size={15} />} onClick={() => onEdit(vinculo)}>
          Editar
        </Button>
        <Button
          size="sm"
          variant="ghost"
          flex="1 0 auto"
          color="danger-text"
          leftIcon={<Trash2 size={15} />}
          onClick={() => onDelete(vinculo)}
        >
          Excluir
        </Button>
      </HStack>
    </Box>
  );
}
